import React, { useState, useEffect, useContext } from 'react';
import { BiCheckCircle, BiXCircle, BiLoaderAlt } from 'react-icons/bi';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { SettingsContext } from '../../context/SettingsProvider';

const formatDate = (dateTimeString) => {
  const date = new Date(dateTimeString);
  return date.toISOString().split('T')[0];
};

const formatTime = (dateTimeString) => {
  const date = new Date(dateTimeString);
  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const period = date.getHours() >= 12 ? 'PM' : 'AM';
  return `${hours}:${minutes}\u00A0${period}`;
};

const formatDuration = (startTime, endTime) => {
  if (!endTime) return '—';
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end - start;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds} sec`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hr`;
};

const Roster = () => {
  const [syncType, setSyncType] = useState('Member');
  const [identifier, setIdentifier] = useState('');
  const [records, setRecords] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getMemberSyncRecords, getOfficeSyncRecords, executeSync, executeFullSync } = useContext(SettingsContext);

  useEffect(() => {
    fetchRecords();
  }, [syncType, getMemberSyncRecords, getOfficeSyncRecords]);

  const fetchRecords = async () => {
    try {
      const syncRecords = syncType === 'Member'
        ? await getMemberSyncRecords()
        : await getOfficeSyncRecords();
      setRecords(syncRecords);
    } catch (error) {
      console.error('Failed to fetch sync records:', error);
    }
  };

  const handleSync = async () => {
    try {
      const tempRecord = {
        startTime: new Date().toISOString(),
        endTime: null,
        type: syncType,
        scope: identifier.trim() ? 'Specific' : 'Incremental',
        success: false,
        running: true,
      };

      setRecords((prevRecords) => [tempRecord, ...prevRecords]);

      const result = await executeSync(syncType, identifier.trim());

      if (result.success) {
        await fetchRecords();
      } else {
        setRecords((prevRecords) =>
          prevRecords.map((record, index) =>
            index === 0 ? { ...record, running: false, success: false } : record
          )
        );
      }

      setIdentifier('');
    } catch (error) {
      console.error('Sync operation failed:', error);
      setRecords((prevRecords) =>
        prevRecords.map((record, index) =>
          index === 0 ? { ...record, running: false, success: false } : record
        )
      );
      await fetchRecords();
    }
  };

  const handleFullSync = async () => {
    try {
      const tempRecord = {
        startTime: new Date().toISOString(),
        endTime: null,
        type: syncType,
        scope: 'Full',
        success: false,
        running: true,
      };

      setRecords((prevRecords) => [tempRecord, ...prevRecords]);

      const result = await executeFullSync(syncType);

      if (result.success) {
        await fetchRecords();
      } else {
        setRecords((prevRecords) =>
          prevRecords.map((record, index) =>
            index === 0 ? { ...record, running: false, success: false } : record
          )
        );
      }
    } catch (error) {
      console.error('Full sync failed:', error);
      setRecords((prevRecords) =>
        prevRecords.map((record, index) =>
          index === 0 ? { ...record, running: false, success: false } : record
        )
      );
      await fetchRecords();
    }
    setIsModalOpen(false);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleIdentifierChange = (e) => {
    const newValue = e.target.value;
    const commaCount = (newValue.match(/,/g) || []).length;
    if (commaCount <= 25) {
      setIdentifier(newValue);
    }
  };

  const handleSyncTypeChange = (type) => {
    setSyncType(type);
    setIdentifier('');
  };

  return (
    <div id="roster" className="w-full px-4 py-6 sm:w-3/4 sm:px-12">
      <div className="max-w-4xl rounded-lg bg-white p-6 shadow-lg dark:bg-stone-900">
        <div className="flex items-center justify-between">
          <h2 className="mb-4 text-2xl font-semibold text-gray-800 dark:text-gray-300">
            Sync Records
          </h2>
          <button
            onClick={toggleModal}
            className="text-xs rounded-md border border-red-500 py-1 px-2 font-semibold text-red-500 hover:text-red-700 hover:border-red-700 bg-transparent"
          >
            Run Full Sync
          </button>
        </div>

        <div className="mb-6 space-y-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Sync Type:
            </label>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleSyncTypeChange('Member')}
                className={`px-4 py-2 rounded-md font-semibold ${
                  syncType === 'Member'
                    ? 'bg-blue-600 text-white dark:bg-indigo-500'
                    : 'bg-gray-200 text-gray-800 dark:bg-stone-700 dark:text-gray-300'
                }`}
              >
                Member
              </button>
              <button
                onClick={() => handleSyncTypeChange('Office')}
                className={`px-4 py-2 rounded-md font-semibold ${
                  syncType === 'Office'
                    ? 'bg-blue-600 text-white dark:bg-indigo-500'
                    : 'bg-gray-200 text-gray-800 dark:bg-stone-700 dark:text-gray-300'
                }`}
              >
                Office
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <input
              type="text"
              placeholder={`Enter ${syncType} Numbers (comma-separated, optional)`}
              value={identifier}
              onChange={handleIdentifierChange}
              className="w-1/2 rounded-lg border-2 border-gray-300 p-2 text-gray-800 dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            />
            <button
              onClick={handleSync}
              className="w-32 rounded-md bg-blue-600 py-2.5 font-semibold text-white transition-colors duration-200 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Sync
            </button>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full border-collapse rounded-lg overflow-hidden shadow-lg">
            <thead>
            <tr className="bg-gray-200 dark:bg-stone-900 text-gray-700 dark:text-gray-100">
              <th className="px-4 py-2 text-left vendor-table-label">Date</th>
              <th className="px-4 py-2 text-left vendor-table-label">Start</th>
              <th className="px-4 py-2 text-left vendor-table-label">End</th>
              <th className="px-4 py-2 text-left vendor-table-label">Duration</th>
              <th className="px-4 py-2 text-left vendor-table-label">Scope</th>
              <th className="px-4 py-2 text-left vendor-table-label">Status</th>
            </tr>
            </thead>
            <tbody>
            {records.map((record, index) => (
              <tr key={index} className="border-b dark:border-stone-700 bg-white dark:bg-stone-800">
                <td className="px-4 py-2 text-gray-800 dark:text-gray-300 whitespace-nowrap">{formatDate(record.startTime)}</td>
                <td className="px-4 py-2 text-gray-800 dark:text-gray-300 whitespace-nowrap">{formatTime(record.startTime)}</td>
                <td className="px-4 py-2 text-gray-800 dark:text-gray-300 whitespace-nowrap">
                  {record.endTime ? formatTime(record.endTime) : '—'}
                </td>
                <td className="px-4 py-2 text-gray-800 dark:text-gray-300">
                  {formatDuration(record.startTime, record.endTime)}
                </td>
                <td className="px-4 py-2 text-gray-800 dark:text-gray-300">{record.scope}</td>
                <td className="px-4 py-2 text-gray-800 dark:text-gray-300">
                  {record.running ? (
                    <div className="flex items-center text-yellow-600 dark:text-yellow-400">
                      <BiLoaderAlt className="mr-2 animate-spin" />
                      Running
                    </div>
                  ) : record.success ? (
                    <div className="flex items-center text-green-600 dark:text-green-400">
                      <BiCheckCircle className="mr-2" />
                      Success
                    </div>
                  ) : (
                    <div className="flex items-center text-red-600 dark:text-red-400">
                      <BiXCircle className="mr-2" />
                      Failed
                    </div>
                  )}
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <ConfirmModal
          title="Confirm Full Sync"
          onConfirm={handleFullSync}
          onCancel={toggleModal}
        >
          <p className="text-sm text-red-500">
            Warning: Running a full sync will initiate a sync for the entire roster, which may be resource-intensive.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default Roster;